import { NgForOf, NgIf } from '@angular/common';
import { Component, inject, Input, OnChanges, OnInit, signal, Signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';
import { first } from 'rxjs/operators';
import { AccommodationsService } from '../../services';
import { QuoteToolLiteStore } from '../../store';
import { Accommodation, AccommodationType, HOMESTAY, OTHER } from '../../types';
import { CardContainerComponent, OptionCardComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { Flows } from '@lx/quote-tool/lite/shared/types';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { OperationKey } from '../../constants';

@Component({
  standalone: true,
  selector: 'lx-accommodations-type',
  templateUrl: './accommodations-type.component.html',
  styleUrls: ['./accommodations-type.component.scss'],
  imports: [NgForOf, RadioButtonModule, FormsModule, OptionCardComponent, CardContainerComponent, NgIf],
})
export class AccommodationsTypeComponent implements OnInit, OnChanges {
  /**
   * List of accommodation options to display
   */
  @Input() accommodations: Accommodation[] = [];

  /**
   * Flag that indicates if selecting one accommodation is optional
   */
  @Input() optional = false;

  protected selectedAccommodation: Accommodation | undefined;
  protected loading: Signal<boolean> = signal(false);

  private ayaFlow: boolean | undefined;
  private otherAccommodationOption: Accommodation | undefined;

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly accommodationsService = inject(AccommodationsService);
  private readonly loadingService = inject(LoadingService);

  ngOnInit(): void {
    this.initComponentFromStore();
    this.otherAccommodationOption = this.accommodationsService.getOtherAccommodationOption();
    this.loading = this.loadingService.isOperationLoading(OperationKey.extrasSummary);
  }

  ngOnChanges({ accommodations }: SimpleChanges): void {
    if (accommodations?.currentValue?.length) {
      this.setAccommodation(this.selectedAccommodation?.type);
      this.updateStoreWithSelectedAccommodation(this.selectedAccommodation);
    }
  }

  setSelectedAccommodation(selectedAccommodation: Accommodation): void {
    this.selectedAccommodation = selectedAccommodation ?? this.otherAccommodationOption;
    this.updateStoreWithSelectedAccommodation(this.selectedAccommodation);
  }

  private initComponentFromStore() {
    this.quoteToolLiteStore.activeFlow$.pipe(first()).subscribe((flow) => (this.ayaFlow = flow === Flows.Aya));

    this.quoteToolLiteStore.selectedAccommodation$.pipe(first()).subscribe((selectedAccommodation: Accommodation) => {
      this.selectedAccommodation = selectedAccommodation;
    });
  }

  private setAccommodation(accommodationType: AccommodationType) {
    if (accommodationType === OTHER) {
      this.selectedAccommodation = this.otherAccommodationOption;
      return;
    }

    // Default to HOMESTAY if no type is selected
    const typeToUse = accommodationType || HOMESTAY;

    // For AYA flow, code is the same as type
    const code = this.ayaFlow ? typeToUse : this.getAccommodation(typeToUse);
    this.selectedAccommodation = { type: typeToUse, code };
  }

  private getAccommodation(value: string) {
    return this.accommodations?.find((accommodation) => accommodation?.type === value)?.code;
  }

  private updateStoreWithSelectedAccommodation(selectedAccommodation: Accommodation) {
    const code = this.ayaFlow ? undefined : selectedAccommodation?.code;
    this.quoteToolLiteStore.updateState({
      selectedAccommodation: { type: selectedAccommodation?.type, code },
    });
  }
}
